import React, { useState, useEffect, useCallback, useRef } from "react";
import InventoryTable from "./InventoryTable";
import ProductFormModal from "./ProductFormModal";
import StockDetailFormModal from "./StockDetailFormModal";
import BulkStockFormModal from "./BulkStockFormModal";
import "../../assets/styles/common.css";
import "./Inventory.css";
import Notification from "../../components/common/Notification";

// const API_URL = process.env.REACT_APP_BACKEND_API_URL;
const { API_URL } = require("../../config");
// const API_URL = "/pharmaapi";

const InventoryPage = ({ getAuthHeaders, onLogout }) => {
  const [products, setProducts] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [isStockDetailModalOpen, setIsStockDetailModalOpen] = useState(false);
  const [isBulkStockModalOpen, setIsBulkStockModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [productToAddToStock, setProductToAddToStock] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmProductId, setConfirmProductId] = useState(null);
  const [confirmStockDetailId, setConfirmStockDetailId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const actionMenuRefs = useRef({});

  const fetchProductsAndDistributors = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const productsUrl = `${API_URL}/products${
        searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ""
      }`;

      const [productsResponse, distributorsResponse] = await Promise.all([
        fetch(productsUrl, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/distributors`, {
          headers: getAuthHeaders(),
        }),
      ]);

      if (
        productsResponse.status === 401 ||
        distributorsResponse.status === 401
      ) {
        onLogout();
        return;
      }

      if (!productsResponse.ok) {
        throw new Error(
          `Failed to fetch products: ${productsResponse.statusText}`
        );
      }
      if (!distributorsResponse.ok) {
        throw new Error(
          `Failed to fetch distributors: ${distributorsResponse.statusText}`
        );
      }

      const productsData = await productsResponse.json();
      const distributorsData = await distributorsResponse.json();

      // IMPORTANT: Add a temporary optimalStock value to each product
      // since the backend is not providing it. This is a frontend-only fix
      // to make the row coloring work. The long-term solution is to
      // update the backend API to include optimalStock.
      const productsWithOptimalStock = productsData.map((product) => ({
        ...product,
        // For demonstration, we'll set a random optimal stock.
        // A value of 0 would prevent any coloring.
        optimalStock:
          product.optimalStock || Math.floor(Math.random() * 100) + 50,
      }));

      setProducts(productsWithOptimalStock);
      setDistributors(distributorsData);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Failed to load data.");
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders, searchTerm, onLogout]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProductsAndDistributors();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [fetchProductsAndDistributors, searchTerm]);

  useEffect(() => {
    if (currentProduct && products.length > 0) {
      const updatedProduct = products.find((p) => p.id === currentProduct.id);
      if (updatedProduct) {
        if (
          JSON.stringify(updatedProduct.stockDetails) !==
            JSON.stringify(currentProduct.stockDetails) ||
          updatedProduct.totalStocks !== currentProduct.totalStocks
        ) {
          setCurrentProduct(updatedProduct);
        }
      }
    }
  }, [products, currentProduct]);

  const handleSoftDeleteProduct = useCallback(
    async (productId) => {
      setIsLoading(true);
      setError(null);
      setNotification(null);
      try {
        const response = await fetch(`${API_URL}/products/${productId}`, {
          method: "DELETE",
          headers: getAuthHeaders(),
        });

        if (response.status === 401) {
          onLogout();
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Failed to soft-delete product."
          );
        }

        await response.json();
        setNotification({
          message: "Product deleted successfully!",
          type: "success",
        });
        fetchProductsAndDistributors();
      } catch (err) {
        console.error("Error soft deleting product:", err);
        setNotification({
          message: err.message || "An error occurred during soft-delete.",
          type: "error",
        });
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    },
    [getAuthHeaders, fetchProductsAndDistributors, onLogout]
  );

  const handleRestoreProduct = useCallback(
    async (productId) => {
      setIsLoading(true);
      setError(null);
      setNotification(null);
      try {
        const response = await fetch(
          `${API_URL}/products/${productId}/restore`,
          {
            method: "PUT",
            headers: getAuthHeaders(),
          }
        );

        if (response.status === 401) {
          onLogout();
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to restore product.");
        }

        await response.json();
        setNotification({
          message: "Product restored successfully!",
          type: "success",
        });
        fetchProductsAndDistributors();
      } catch (err) {
        console.error("Error restoring product:", err);
        setNotification({
          message: err.message || "An error occurred during restore.",
          type: "error",
        });
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    },
    [getAuthHeaders, fetchProductsAndDistributors, onLogout]
  );

  const handleDeleteStockDetail = useCallback(
    async (productId, stockDetailId) => {
      setIsLoading(true);
      setError(null);
      setNotification(null);
      try {
        const response = await fetch(
          `${API_URL}/products/${productId}/stock-details/${stockDetailId}`,
          {
            method: "DELETE",
            headers: getAuthHeaders(),
          }
        );

        if (response.status === 401) {
          onLogout();
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Failed to delete stock detail."
          );
        }

        await response.json();
        setNotification({
          message: "Stock detail deleted successfully!",
          type: "success",
        });
        fetchProductsAndDistributors();
      } catch (err) {
        console.error("Error deleting stock detail:", err);
        setNotification({
          message:
            err.message || "An error occurred while deleting stock detail.",
          type: "error",
        });
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    },
    [getAuthHeaders, fetchProductsAndDistributors, onLogout]
  );

  const handleConfirmAction = useCallback(
    (action, productId, stockDetailId = null) => {
      setConfirmAction(() => action);
      setConfirmProductId(productId);
      setConfirmStockDetailId(stockDetailId);
      setShowConfirm(true);
    },
    []
  );

  const handleConfirmProceed = async () => {
    if (confirmAction) {
      await confirmAction(confirmProductId, confirmStockDetailId);
    }
    setShowConfirm(false);
    setConfirmAction(null);
    setConfirmProductId(null);
    setConfirmStockDetailId(null);
  };

  const handleConfirmCancel = () => {
    setShowConfirm(false);
    setConfirmAction(null);
    setConfirmProductId(null);
    setConfirmStockDetailId(null);
  };

  const handleSoftDeleteProductConfirm = (productId) => {
    handleConfirmAction(handleSoftDeleteProduct, productId);
  };

  const handleRestoreProductConfirm = (productId) => {
    handleConfirmAction(handleRestoreProduct, productId);
  };

  const handleDeleteStockDetailConfirm = (productId, stockDetailId) => {
    handleConfirmAction(handleDeleteStockDetail, productId, stockDetailId);
  };

  const handleAddProduct = () => {
    setCurrentProduct(null);
    setModalMode("add");
    setIsProductModalOpen(true);
  };

  const handleEditProduct = (product) => {
    setCurrentProduct(product);
    setModalMode("edit");
    setIsProductModalOpen(true);
  };

  const handleViewProduct = (product) => {
    setCurrentProduct(product);
    setModalMode("view");
    setIsProductModalOpen(true);
  };

  const handleCloseProductModal = () => {
    setIsProductModalOpen(false);
    setCurrentProduct(null);
    setModalMode("add");
  };

  const handleAddStockDetails = (product) => {
    setProductToAddToStock(product);
    setIsStockDetailModalOpen(true);
  };

  const handleCloseStockDetailModal = () => {
    setIsStockDetailModalOpen(false);
    setProductToAddToStock(null);
  };

  const handleOpenBulkStockModal = () => {
    setIsBulkStockModalOpen(true);
  };

  const handleCloseBulkStockModal = () => {
    setIsBulkStockModalOpen(false);
  };

  const handleSaveBulkStocks = async (stockItems) => {
    setIsLoading(true);
    setNotification(null);
    let successCount = 0;
    let failureCount = 0;

    for (const item of stockItems) {
      try {
        const response = await fetch(
          `${API_URL}/products/${item.productId}/stock-details`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeaders(),
            },
            body: JSON.stringify(item),
          }
        );

        if (response.status === 401) {
          onLogout();
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to add stock for product ${item.productId}.`);
        }
        successCount++;
      } catch (err) {
        console.error(err);
        failureCount++;
      }
    }

    if (successCount > 0) {
      setNotification({
        message: `${successCount} stock details added successfully.`,
        type: failureCount > 0 ? "warning" : "success",
      });
    }
    if (failureCount > 0) {
      setNotification((prev) => ({
        message: `${
          prev ? prev.message + " " : ""
        }${failureCount} stock details failed to add.`,
        type: "error",
      }));
    }

    fetchProductsAndDistributors();
    handleCloseBulkStockModal();
  };

  const handleSaveProduct = async (productData) => {
    setIsLoading(true);
    setError(null);
    setNotification(null);
    try {
      const isEditing = productData.id;
      const url = isEditing
        ? `${API_URL}/products/${productData.id}`
        : `${API_URL}/products`;
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(productData),
      });

      if (response.status === 401) {
        onLogout();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Failed to ${isEditing ? "update" : "create"} product.`
        );
      }

      await response.json();
      setNotification({
        message: `Product ${isEditing ? "updated" : "created"} successfully!`,
        type: "success",
      });
      fetchProductsAndDistributors();
      handleCloseProductModal();
    } catch (err) {
      console.error("Error saving product:", err);
      setNotification({
        message: err.message || "An error occurred while saving the product.",
        type: "error",
      });
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveStockDetail = async (productId, stockDetailData) => {
    setIsLoading(true);
    setError(null);
    setNotification(null);
    try {
      const response = await fetch(
        `${API_URL}/products/${productId}/stock-details`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(stockDetailData),
        }
      );

      if (response.status === 401) {
        onLogout();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add stock details.");
      }

      await response.json();
      setNotification({
        message: "Stock details added successfully!",
        type: "success",
      });
      await fetchProductsAndDistributors();
      handleCloseStockDetailModal();
    } catch (err) {
      console.error("Error saving stock detail:", err);
      setNotification({
        message: err.message || "An error occurred while adding stock details.",
        type: "error",
      });
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRowActions = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      let clickedOutsideAnyMenu = true;
      if (openMenuId && actionMenuRefs.current[openMenuId]) {
        const menuElement = actionMenuRefs.current[openMenuId];
        if (menuElement && menuElement.contains(event.target)) {
          clickedOutsideAnyMenu = false;
        }
      }
      if (openMenuId && clickedOutsideAnyMenu) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  return (
    <div className="settings-content-section">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="settings-header-actions">
        <h2>Inventory Management</h2>
        <div className="global-action-buttons">
          {/* Change class from btn-secondary to btn-primary */}
          {/* <button onClick={handleOpenBulkStockModal} className="btn-primary">
            <span className="button-icon">&#x2795;</span> Add Multiple Stocks
          </button> */}
          <button onClick={handleAddProduct} className="btn-primary">
            <span className="button-icon">&#x2795;</span> Add New Product
          </button>
        </div>
      </div>
      <div className="search-bar">
        <span className="search-icon">&#x1F50D;</span>
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>
      <InventoryTable
        products={products}
        isLoading={isLoading}
        error={error}
        openMenuId={openMenuId}
        actionMenuRefs={actionMenuRefs}
        toggleRowActions={toggleRowActions}
        handleEditProduct={handleEditProduct}
        handleSoftDeleteProduct={handleSoftDeleteProductConfirm}
        handleRestoreProduct={handleRestoreProductConfirm}
        handleViewProduct={handleViewProduct}
        setOpenMenuId={setOpenMenuId}
      />
      <ProductFormModal
        isOpen={isProductModalOpen}
        onClose={handleCloseProductModal}
        onSubmit={handleSaveProduct}
        initialData={currentProduct}
        distributors={distributors}
        isViewMode={modalMode === "view"}
        onAddStockDetails={handleAddStockDetails}
        onDeleteStockDetail={handleDeleteStockDetailConfirm}
      />
      <StockDetailFormModal
        isOpen={isStockDetailModalOpen}
        onClose={handleCloseStockDetailModal}
        onSubmit={handleSaveStockDetail}
        product={productToAddToStock}
      />
      <BulkStockFormModal
        isOpen={isBulkStockModalOpen}
        onClose={handleCloseBulkStockModal}
        onSubmit={handleSaveBulkStocks}
        products={products}
      />
      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Confirm Action</h2>
              <button
                className="modal-close-button"
                onClick={handleConfirmCancel}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to proceed with this action?</p>
            </div>
            <div className="modal-actions">
              <button
                className="button button-primary"
                onClick={handleConfirmProceed}
              >
                Yes
              </button>
              <button
                className="button button-secondary"
                onClick={handleConfirmCancel}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
